import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
  useToast,
} from "../../../design-system";
import { grantEmployeeAbsenceAdjustment } from "../../../helpers/kronaly_helper";

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

/**
 * Modal "Agregar días libres".
 *
 * Concede días al saldo del empleado bajo convenio empleado-empleador
 * (discrecional del empleador). Suma por encima del derecho de la política.
 *
 * Props:
 *   - open: boolean
 *   - employeeProfileId: empleado al que se le conceden los días.
 *   - onClose, onCreated
 */
const AddFreeDaysModal = ({ open, employeeProfileId, onClose, onCreated }) => {
  const toast = useToast();
  const [amount, setAmount] = useState("1");
  const [note, setNote] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setAmount("1");
    setNote("");
    setExpiresAt("");
    setError(null);
  }, [open]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!employeeProfileId) {
      setError("No se ha podido identificar el empleado.");
      return;
    }
    const days = Number(amount);
    if (!Number.isFinite(days) || days <= 0) {
      setError("Indica una cantidad de días mayor que cero.");
      return;
    }
    if (!note.trim()) {
      setError("Indica una nota que detalle el convenio.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await grantEmployeeAbsenceAdjustment(employeeProfileId, {
        amount: days,
        note: note.trim(),
        expires_at: expiresAt || null,
      });
      const created = unwrap(r);
      toast.success(
        "Días libres concedidos",
        `${days} día${days === 1 ? "" : "s"} añadido${days === 1 ? "" : "s"} al saldo.`,
      );
      if (onCreated) onCreated(created);
      onClose();
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "No se pudieron conceder los días.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Agregar días libres" size="md">
      <form onSubmit={onSubmit}>
        <ModalBody>
          {error && (
            <div style={{ marginBottom: 12 }}>
              <Alert tone="danger">{error}</Alert>
            </div>
          )}

          <Field label="Días" required help="Se suman al saldo por encima del derecho de la política.">
            <Input
              type="number"
              min={0.5}
              step={0.5}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </Field>

          <div className="kr-mt-3">
            <Field
              label="Nota (convenio)"
              required
              help="Detalla el acuerdo empleado-empleador (p. ej. compensación por horas extra distribuidas)."
            >
              <Input
                type="textarea"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={2000}
                placeholder="Ej. 1 día libre por reorganización de la jornada según convenio del 07/07/2026."
                required
              />
            </Field>
          </div>

          <div className="kr-mt-3">
            <Field
              label="Fecha límite de disfrute (opcional)"
              help="Si se deja vacío, los días se atribuyen al ciclo actual y caducan al cerrarlo."
            >
              <Input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </Field>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving && <Spinner size={12} color="#fff" style={{ marginRight: 6 }} />}
            {saving ? "Concediendo…" : "Conceder días"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default AddFreeDaysModal;
